import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SnackbarService } from '../services/snack-service/snackbar-service';
import { Router } from '@angular/router';
import { isSafeReturnUrl } from '../shared/navigation/return-url.util';
import { DataService } from '../services/data-service';
import { isAppVersionRequestUrl } from '../services/app-version/app-version-check.service';
import { ASSOCIATE_HTTP, isAssociateHttpRequest } from '../associate/auth/associate-http.context';
import { httpErrorUserMessage } from '../shared/http/http-error-user-message';

/** Edit stage dates APIs — errors are handled in the dialog facade. */
function isStageTimelineApi(url: string): boolean {
  return url.includes('/stage-timeline/');
}

function isMemberAllocationHistoryApi(url: string): boolean {
  return url.includes('/employees/') && url.includes('/allocation-history');
}

function isTaskAllocationHistoryApi(url: string): boolean {
  return url.includes('/assignment-history');
}

function isWorkplanChangeOrderApi(url: string): boolean {
  return url.includes('/workplan-phase-change-orders');
}

/** Version history dialog handles empty/error states locally. */
function isWorkplanHistoryApi(url: string): boolean {
  return url.includes('/crm/get-workplan-history');
}

/** Change-order approval gate — failures render inline in the change order modal, never redirect. */
function isChangeOrderStatusApi(url: string): boolean {
  return url.includes('/inbox/ra/change-order-status');
}

function isCrmLeadOpportunityWriteApi(url: string): boolean {
  return (
    url.includes('/crm/add-lead') ||
    url.includes('/crm/edit-lead') ||
    url.includes('/crm/add-opportunity') ||
    url.includes('/crm/update-opportunity') ||
    url.includes('/crm/edit-opportunity-details') ||
    url.includes('/crm/convert-to-opportunity')
  );
}

function isFeatureDisabledHttpError(error: HttpErrorResponse): boolean {
  return error.status === 0 && error.statusText === 'Feature disabled';
}

/** Assignment history modal renders its own 400 / 403 / 500 states. */
function isAssignmentActivityApi(url: string): boolean {
  return url.includes('/asset/get-assignment-activity');
}

/** Manage assignment shows the API validation message itself. */
function isSoftwareBatchAssignmentApi(url: string): boolean {
  return url.includes('/asset/sync-software-batch-assignments');
}

/** Public client proposal-review links — the page renders its own status cards. */
function isPublicProposalReviewApi(url: string): boolean {
  return (
    url.includes('/external/get-document-by-token') ||
    url.includes('/external/proposal/')
  );
}

function isInvoiceSubmitForApprovalApi(url: string): boolean {
  return url.includes('/invoice/') && url.includes('/submit-for-approval');
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly dataService = inject(DataService);

  constructor(private snackbarService: SnackbarService, private router: Router) { }
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (isAppVersionRequestUrl(req.url) || isAssociateHttpRequest(req.context.get(ASSOCIATE_HTTP))) {
          return throwError(() => error);
        }

        if (isFeatureDisabledHttpError(error)) {
          return throwError(() => error);
        }

        let errorMsg = '';

        console.log('Error intercepted:', error);

        if (error.error instanceof ErrorEvent) {
          // Client-side error
          errorMsg = `Client-side error: ${error.error.message}`;
        } else {
          const skipGlobalSnackbar =
            req.url.includes('assign-asset-software') ||
            req.url.includes('add-billingcycle') ||
            req.url.includes('add-vendor') ||
            req.url.includes('/roleandpermission/delete-role') ||
            isStageTimelineApi(req.url) ||
            isPublicProposalReviewApi(req.url) ||
            isMemberAllocationHistoryApi(req.url) ||
            isTaskAllocationHistoryApi(req.url) ||
            isWorkplanChangeOrderApi(req.url) ||
            isWorkplanHistoryApi(req.url) ||
            isChangeOrderStatusApi(req.url) ||
            isCrmLeadOpportunityWriteApi(req.url) ||
            isInvoiceSubmitForApprovalApi(req.url) ||
            isAssignmentActivityApi(req.url) ||
            isSoftwareBatchAssignmentApi(req.url);
          const apiMessage = httpErrorUserMessage(error);

          switch (error.status) {
            case 400:
              errorMsg = apiMessage || 'Bad Request. Please check your input.';
              console.log(error);

              if (!skipGlobalSnackbar) {
                this.snackbarService.show('Error!', errorMsg, error.status);
              }
              break;
            case 401:
              errorMsg = apiMessage || 'Unauthorized. Please login again.';
              if (!skipGlobalSnackbar) {
                this.snackbarService.show('Error!', errorMsg, error.status);
              }
              const skipRedirectApis = ['/api/user/change-password', '/api/user/complete-password-action'
              ];
              const shouldSkipRedirect =
                skipRedirectApis.some(api => req.url.includes(api)) ||
                isPublicProposalReviewApi(req.url);
              if (!shouldSkipRedirect) {
                this.dataService.clearClientSession();
                const attempted = this.router.url;
                const queryParams = isSafeReturnUrl(attempted) ? { returnUrl: attempted } : {};
                void this.router.navigate(['/login'], { queryParams });
              }
              break;
            case 403:
              errorMsg = 'Forbidden. You do not have access to this resource.';
              const isAssetApi = /\/api\/.*asset|\/assets\//i.test(req.url) || req.url.includes('it-asset');
              const isBudgetApi = /\/api\/.*budget|\/budget\//i.test(req.url);
              const isInboxApi = /\/api\/.*inbox|\/inbox\//i.test(req.url) || /\/api\/.*approval/i.test(req.url);
              const message = isAssetApi ? "You don't have permission to perform this action." : (apiMessage || errorMsg);
              if (!skipGlobalSnackbar) {
                this.snackbarService.show('Error!', message, error.status);
              }
              if (
                (isAssetApi || isBudgetApi || isInboxApi) &&
                !isAssignmentActivityApi(req.url) &&
                !isChangeOrderStatusApi(req.url)
              ) {
                this.router.navigate(['/dashboard/home']);
              }
              break;
            case 404:
              errorMsg = apiMessage || 'Resource not found.';
              if (
                !skipGlobalSnackbar &&
                error?.error?.message !== 'No workplan found for this opportunity' &&
                !(
                  req.url.includes('/billing-plan') &&
                  req.method === 'GET' &&
                  (error?.error?.message === 'Billing plan not found' ||
                    error?.error?.message === 'Opportunity not found in your organization')
                )
              ) {
                this.snackbarService.show('Error!', errorMsg, error.status);
              }
              break;
            case 409:
            case 410:
            case 422:
              errorMsg =
                (typeof error?.error?.message === 'string' && error.error.message) ||
                (error.status === 410
                  ? 'Preview expired. Please review changes again.'
                  : error.status === 409
                    ? 'This stage was updated elsewhere. Reloading…'
                    : 'Invalid request.');
              if (!skipGlobalSnackbar) {
                this.snackbarService.show('Error!', errorMsg, error.status);
              }
              break;
            case 500:
              errorMsg = 'Something went wrong! Please try again.';
              if (!skipGlobalSnackbar) {
                this.snackbarService.show('Error!', error.error.message || errorMsg, error.status);
              }
              break;
            default:
              errorMsg = `Unexpected error occurred, please try again.`;
              if (!skipGlobalSnackbar) {
                this.snackbarService.show('Error!', error.error.message || errorMsg, 404);
              }
              break;
          }
          // this.snackbarService.show('Error!',errorMsg,error.status);
        }
        // Optionally, you can show a toast, redirect, etc.
        // console.error('Error intercepted:', errorMsg);

        // Rethrow HttpErrorResponse so callers can read `error.error` (e.g. validation payloads).
        const shouldPreserveHttpError =
          req.url.includes('bulk-upload/upload-profile') ||
          req.url.includes('/data-import/upcoming-to-ongoing-project/upload-excel') ||
          req.url.includes('support-project-timesheets') ||
          req.url.includes('project-timesheet') ||
          req.url.includes('assign-asset-software') ||
          req.url.includes('add-billingcycle') ||
          req.url.includes('add-vendor') ||
          req.url.includes('/roleandpermission/delete-role') ||
          isStageTimelineApi(req.url) ||
          isPublicProposalReviewApi(req.url) ||
          isMemberAllocationHistoryApi(req.url) ||
          isWorkplanChangeOrderApi(req.url) ||
          isCrmLeadOpportunityWriteApi(req.url) ||
          isInvoiceSubmitForApprovalApi(req.url) ||
          isAssignmentActivityApi(req.url) ||
          isSoftwareBatchAssignmentApi(req.url);

        if (shouldPreserveHttpError) {
          return throwError(() => error);
        } else {
          // Keep the API message when wrapping so callers don't lose it.
          return throwError(() => new Error(errorMsg || 'Unexpected error occurred, please try again.'));
        }
      })
    );
  }
}